import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLiveEvents } from "@/hooks/useLiveEvents";
import { Button } from "@/components/ui/button";
import { AvatarUploader } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import {
  Inbox, Wifi, Users, Users2, BarChart3, Zap, CalendarClock,
  LogOut, Sun, Moon, Volume2, VolumeX, Building2, Settings, Briefcase, CalendarDays, Building, ShieldAlert,
} from "lucide-react";
import { TimeclockWidget } from "@/components/TimeclockWidget";
import { useChatAlerts, NavItems } from "@/components/NavAlerts";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { TenantLogo } from "@/pages/PlatformPage";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { playBip, playAlert } from "@/lib/sound";

const NAV = [
  { to: "/inbox", label: "Atendimentos", icon: Inbox, testid: "dashboard-nav-inbox" },
  { to: "/connections", label: "Conexões", icon: Wifi, testid: "dashboard-nav-connections" },
  { to: "/contacts", label: "Contatos", icon: Users, testid: "dashboard-nav-contacts" },
  { to: "/crm", label: "CRM e Tarefas", icon: Briefcase, testid: "dashboard-nav-crm" },
  { to: "/agenda", label: "Agenda", icon: CalendarDays, testid: "dashboard-nav-agenda" },
  { to: "/quick-replies", label: "Respostas Rápidas", icon: Zap, testid: "dashboard-nav-quick" },
  { to: "/scheduler", label: "Agendador", icon: CalendarClock, testid: "dashboard-nav-scheduler" },
  { to: "/users", label: "Atendentes", icon: Users2, testid: "dashboard-nav-users", admin: true },
  { to: "/departments", label: "Setores", icon: Building2, testid: "dashboard-nav-departments", admin: true },
  { to: "/reports", label: "Relatórios", icon: BarChart3, testid: "dashboard-nav-reports" },
  { to: "/settings", label: "Configurações", icon: Settings, testid: "dashboard-nav-settings", admin: true },
  { to: "/platform", label: "Empresas", icon: Building, testid: "dashboard-nav-platform", master: true },
];

const ImpersonationBar = ({ user, refresh }) => {
  if (!user?.impersonating) return null;
  const exit = async () => { await api.post("/platform/exit"); await refresh(); window.location.href = "/platform"; };
  return (
    <div className="fixed top-0 inset-x-0 z-50 h-9 bg-amber-500 text-amber-950 text-xs font-semibold flex items-center justify-center gap-3" data-testid="impersonation-bar">
      <ShieldAlert className="w-4 h-4" /> Modo suporte: você está no painel de {user.tenant?.name}
      <button onClick={exit} className="underline" data-testid="impersonation-exit">Voltar para minha empresa</button>
    </div>
  );
};

export default function AppShell() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [dark, setDark] = useState(() => localStorage.getItem("cz_theme") === "dark");
  const [alerts, reloadAlerts] = useChatAlerts();
  const [menuOpen, setMenuOpen] = useState(false);
  const navItems = NAV.filter(n => (n.master ? user?.platform_role === "master" : user?.role === "admin" || !n.admin));

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("cz_theme", dark ? "dark" : "light");
  }, [dark]);

  useLiveEvents((payload) => {
    if (payload.event === "message" && !payload.data.from_me && !payload.data.is_note) {
      playBip();
      toast(`Nova mensagem: ${payload.data.sender_name || "cliente"}`, {
        description: (payload.data.body || "").slice(0, 60),
      });
    }
    if (payload.event === "chat_updated") reloadAlerts();
    if (payload.event === "wait_alert" && user?.role === "admin") {
      playAlert();
      toast.error(`${payload.data.chat.contact_name} está sem resposta há mais de ${payload.data.minutes} min`, {
        description: payload.data.chat.assignee_name ? `Responsável: ${payload.data.chat.assignee_name}` : "Sem atendente",
        duration: 20000,
        action: { label: "Abrir", onClick: () => navigate(`/inbox/${payload.data.chat.id}`) },
      });
    }
    if (payload.event === "chat_transferred" && payload.data.to_user_id === user?.id) {
      playAlert();
      reloadAlerts();
      toast.warning(`Novo atendimento transferido para você: ${payload.data.chat.contact_name}`, {
        description: `Por ${payload.data.by}${payload.data.reason ? ` · ${payload.data.reason}` : ""}`,
        duration: 15000,
        action: { label: "Abrir", onClick: () => navigate(`/inbox/${payload.data.chat.id}`) },
      });
    }
    if (payload.event === "connection_update" && payload.data.status === "connected") {
      toast.success(`Conexão ativa · ${payload.data.phone || ""}`);
    }
  });

  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("cz_sound") !== "0");
  const toggleSound = () => {
    const next = !soundOn;
    localStorage.setItem("cz_sound", next ? "1" : "0");
    setSoundOn(next);
    if (next) playBip();
  };

  return (
    <div className={`h-screen w-screen flex overflow-hidden bg-background text-foreground ${user?.impersonating ? "pt-9" : ""}`}>
      <ImpersonationBar user={user} refresh={refresh} />
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 flex-col border-r border-border bg-card">
        <div className="p-5 flex items-center gap-3" data-testid="tenant-brand">
          <TenantLogo tenant={user?.tenant} />
          <div className="min-w-0">
            <div className="heading font-bold truncate" data-testid="tenant-brand-name">{user?.tenant?.name || "ContaZap"}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">ContaZap - Vendor</div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          <NavItems items={navItems} alerts={alerts} />
        </nav>

        <TimeclockWidget />

        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <AvatarUploader user={user} endpoint="/users/me/avatar" onChange={refresh} className="w-9 h-9" testid="my-avatar-upload" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">{user?.role}</Badge>
                {user?.department && <span className="text-[11px] text-muted-foreground truncate">{user.department}</span>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button variant="outline" size="sm" className="flex-1" onClick={toggleSound} data-testid="sound-toggle" title="Som de novas mensagens">
              {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={() => setDark(d => !d)} data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" className="flex-1" onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className={`md:hidden fixed ${user?.impersonating ? "top-9" : "top-0"} inset-x-0 z-30 h-14 bg-card border-b border-border flex items-center px-4 justify-between`}>
        <div className="flex items-center gap-2 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-button" className="relative">
            <Menu className="w-5 h-5" />
            {(alerts.unattended > 0 || alerts.transferred_to_me > 0) && <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-rose-600 animate-pulse" />}
          </Button>
          <TenantLogo tenant={user?.tenant} className="w-8 h-8" />
          <div className="heading font-bold truncate">{user?.tenant?.name || "ContaZap"}</div>
        </div>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }}>
          <LogOut className="w-4 h-4" />
        </Button>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-72 p-0 flex flex-col" data-testid="mobile-menu">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-left"><TenantLogo tenant={user?.tenant} className="w-8 h-8" /> <span className="truncate">{user?.tenant?.name}</span></SheetTitle>
          </SheetHeader>
          <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
            <NavItems items={navItems} alerts={alerts} onNavigate={() => setMenuOpen(false)} />
          </nav>
          <TimeclockWidget />
          <div className="p-3 border-t border-border flex items-center gap-3">
            <AvatarUploader user={user} endpoint="/users/me/avatar" onChange={refresh} className="w-9 h-9" testid="mobile-avatar-upload" />
            <div className="flex-1 min-w-0 text-sm font-semibold truncate">{user?.name}</div>
            <Button variant="outline" size="sm" onClick={toggleSound} data-testid="mobile-sound-toggle">{soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}</Button>
            <Button variant="outline" size="sm" onClick={() => setDark(d => !d)} data-testid="mobile-theme-toggle">{dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
          </div>
        </SheetContent>
      </Sheet>

      <main className="flex-1 overflow-hidden md:pt-0 pt-14">
        <Outlet />
      </main>
    </div>
  );
}
